import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Copy, Check, KeyRound, Trash2, ShieldAlert } from "lucide-react";
import { Btn } from "../../components/Btn";
import { Modal } from "../../components/Modal";
import { useToast } from "../../components/Toast";
import {
  createDevToken,
  getDevTokens,
  revokeDevToken,
} from "../../api/endpoints";
import { ApiError, type TokenCreateResponse, type TokenOut } from "../../api/types";

const ALL_SCOPES = [
  { k: "upload",  label: "upload — загрузка APK" },
  { k: "publish", label: "publish — публикация версий" },
  { k: "stats",   label: "stats — чтение статистики" },
] as const;

export default function TokensPage() {
  const push = useToast();
  const qc = useQueryClient();

  const tokensQ = useQuery({
    queryKey: ["dev", "tokens"],
    queryFn: getDevTokens,
  });

  // Активные сверху, отозванные снизу.
  const tokens = useMemo(() => {
    const items = tokensQ.data ?? [];
    return [...items].sort((a, b) => {
      const aRev = a.revoked_at ? 1 : 0;
      const bRev = b.revoked_at ? 1 : 0;
      if (aRev !== bRev) return aRev - bRev;
      return b.created_at.localeCompare(a.created_at);
    });
  }, [tokensQ.data]);

  const [createOpen, setCreateOpen] = useState(false);
  const [revoking, setRevoking] = useState<TokenOut | null>(null);
  const [justCreated, setJustCreated] = useState<TokenCreateResponse | null>(null);

  const createMut = useMutation({
    mutationFn: ({ name, scopes }: { name: string; scopes: string[] }) =>
      createDevToken(name, scopes),
    onSuccess: async (res) => {
      await qc.invalidateQueries({ queryKey: ["dev", "tokens"] });
      setCreateOpen(false);
      setJustCreated(res);
    },
    onError: (e) => {
      const msg = e instanceof ApiError ? (e.message || "Не удалось создать токен") : "Не удалось создать токен";
      push({ kind: "warn", text: msg });
    },
  });

  const revokeMut = useMutation({
    mutationFn: (id: number) => revokeDevToken(id),
    onSuccess: async (res) => {
      await qc.invalidateQueries({ queryKey: ["dev", "tokens"] });
      setRevoking(null);
      push({ kind: "warn", text: `Токен #${res.id} отозван` });
    },
    onError: (e) => {
      const msg = e instanceof ApiError ? (e.message || "Не удалось отозвать токен") : "Не удалось отозвать токен";
      push({ kind: "warn", text: msg });
    },
  });

  return (
    <>
      <header className="flex flex-col gap-3 pb-5 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-[20px] font-semibold leading-tight tracking-[-0.01em] text-[var(--color-fg)] sm:text-[22px]">
            Токены API
          </h1>
          <div className="mt-1 text-[12.5px] text-[var(--color-fg-muted)]">
            Personal access tokens для CI/CD. При создании plain-секрет выдаётся один раз — сохраните сразу.
          </div>
        </div>
        <Btn icon={Plus} variant="primary" onClick={() => setCreateOpen(true)}>Создать токен</Btn>
      </header>

      <section className={"rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 " + (tokensQ.isLoading ? "opacity-70" : "")}>
        <ul className="divide-y divide-[var(--color-border)]">
          {tokens.map((t, i) => (
            <li key={t.id} style={{ animationDelay: `${i * 35}ms` }} className="stagger-up py-3 first:pt-0 last:pb-0">
              <TokenRow token={t} onRevoke={() => setRevoking(t)} />
            </li>
          ))}
          {!tokensQ.isLoading && !tokens.length && (
            <li className="py-8 text-center text-[12.5px] text-[var(--color-fg-muted)]">
              Токенов пока нет. Создайте первый для интеграции с CI.
            </li>
          )}
        </ul>
      </section>

      <CreateModal
        open={createOpen}
        onClose={() => createMut.isPending ? null : setCreateOpen(false)}
        loading={createMut.isPending}
        onSubmit={(name, scopes) => createMut.mutate({ name, scopes })}
      />

      <RevealModal
        token={justCreated}
        onClose={() => setJustCreated(null)}
      />

      <RevokeConfirmModal
        token={revoking}
        loading={revokeMut.isPending}
        onClose={() => revokeMut.isPending ? null : setRevoking(null)}
        onConfirm={() => revoking && revokeMut.mutate(revoking.id)}
      />
    </>
  );
}

function TokenRow({ token, onRevoke }: { token: TokenOut; onRevoke: () => void }) {
  const active = !token.revoked_at;
  return (
    <div className="flex flex-wrap items-start gap-3">
      <span
        className={
          "mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-lg " +
          (active
            ? "bg-[var(--color-success-bg)] text-[var(--color-success)]"
            : "bg-[var(--color-danger-bg)] text-[var(--color-danger)]")
        }
      >
        <KeyRound size={15} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <div className="text-[14px] font-medium text-[var(--color-fg)]">{token.name}</div>
          {!active && (
            <span className="rounded-full bg-[var(--color-danger-bg)] px-2 py-0.5 text-[10.5px] font-medium text-[var(--color-danger)]">
              отозван
            </span>
          )}
        </div>
        <div className="mt-0.5 font-mono text-[11.5px] text-[var(--color-fg-muted)]">#{token.id}</div>
        {!!token.scopes.length && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {token.scopes.map((s) => (
              <span key={s} className="rounded bg-[var(--color-surface-3)] px-1.5 py-0.5 font-mono text-[10.5px] text-[var(--color-fg-muted)]">{s}</span>
            ))}
          </div>
        )}
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11.5px] text-[var(--color-fg-muted)]">
          <span>Создан: <span className="font-mono">{fmtDate(token.created_at)}</span></span>
          <span>
            Использовался:{" "}
            <span className={"font-mono " + (token.last_used_at ? "" : "text-[var(--color-fg-faint)]")}>
              {token.last_used_at ? fmtDate(token.last_used_at) : "никогда"}
            </span>
          </span>
          {token.revoked_at && (
            <span>Отозван: <span className="font-mono">{fmtDate(token.revoked_at)}</span></span>
          )}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {active && (
          <button
            onClick={onRevoke}
            className="grid h-8 w-8 place-items-center rounded-lg text-[var(--color-fg-muted)] hover:bg-[var(--color-danger-bg)] hover:text-[var(--color-danger)]"
            title="Отозвать"
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

function CreateModal({
  open,
  onClose,
  onSubmit,
  loading,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (name: string, scopes: string[]) => void;
  loading: boolean;
}) {
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<Record<string, boolean>>({
    upload: true,
    publish: false,
    stats: false,
  });
  const toggle = (s: string) => setScopes((p) => ({ ...p, [s]: !p[s] }));

  const submit = () => {
    if (!name.trim()) return;
    const selected = Object.entries(scopes).filter(([, v]) => v).map(([k]) => k);
    onSubmit(name.trim(), selected);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Новый токен API"
      subtitle="Plain-секрет будет показан один раз — сразу скопируйте"
      size="sm"
      footer={
        <>
          <Btn onClick={onClose}>Отмена</Btn>
          <Btn variant="primary" onClick={submit}>
            {loading ? "Создание…" : "Создать"}
          </Btn>
        </>
      }
    >
      <div className="space-y-3">
        <label className="block">
          <div className="text-[11px] uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">Название</div>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="GitLab CI · ru.corp.docs"
            className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-[13px] outline-none focus:border-[var(--color-fg-muted)]"
          />
        </label>
        <div>
          <div className="text-[11px] uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">Scope</div>
          <div className="mt-1 flex flex-wrap gap-2">
            {ALL_SCOPES.map((s) => (
              <label key={s.k} className={
                "flex cursor-pointer items-center gap-2 rounded-lg border px-2.5 py-1.5 text-[12px] " +
                (scopes[s.k]
                  ? "border-[var(--color-brand)] bg-[var(--color-brand)]/5 text-[var(--color-fg)]"
                  : "border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-fg-muted)]")
              }>
                <input type="checkbox" checked={!!scopes[s.k]} onChange={() => toggle(s.k)} className="h-3.5 w-3.5 accent-[var(--color-brand)]" />
                <span className="font-mono">{s.label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}

function RevealModal({
  token,
  onClose,
}: {
  token: TokenCreateResponse | null;
  onClose: () => void;
}) {
  const push = useToast();
  const [copied, setCopied] = useState(false);

  const copy = () => {
    if (!token) return;
    navigator.clipboard?.writeText(token.token);
    setCopied(true);
    push({ kind: "success", text: "Токен скопирован в буфер" });
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Modal
      open={!!token}
      onClose={() => {
        setCopied(false);
        onClose();
      }}
      title={<span className="flex items-center gap-2 text-[var(--color-success)]"><KeyRound size={16} />Токен создан</span>}
      subtitle="Это единственный раз, когда вы видите plain-секрет"
      size="sm"
      footer={
        <Btn variant="primary" onClick={() => {
          setCopied(false);
          onClose();
        }}>
          Я сохранил токен
        </Btn>
      }
    >
      {token && (
        <div className="space-y-3">
          <div className="rounded-lg border border-[var(--color-warning)]/40 bg-[var(--color-warning-bg)] px-3 py-2 text-[12px] text-[var(--color-warning)]">
            <ShieldAlert size={12} className="mr-1 inline" />
            Сохраните токен сейчас — после закрытия его невозможно восстановить.
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">{token.name} · #{token.id}</div>
            <div className="mt-1 flex items-stretch gap-2">
              <code className="flex-1 truncate rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] px-3 py-2 font-mono text-[12.5px] text-[var(--color-fg)]">
                {token.token}
              </code>
              <button
                onClick={copy}
                className="grid place-items-center rounded-lg border border-[var(--color-border-strong)] bg-[var(--color-surface)] px-3 text-[var(--color-fg)] transition-colors hover:bg-[var(--color-surface-2)]"
                title="Скопировать"
              >
                {copied ? <Check size={14} className="text-[var(--color-success)]" /> : <Copy size={14} />}
              </button>
            </div>
            {!!token.scopes.length && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {token.scopes.map((s) => (
                  <span key={s} className="rounded bg-[var(--color-surface-3)] px-1.5 py-0.5 font-mono text-[10.5px] text-[var(--color-fg-muted)]">{s}</span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

function RevokeConfirmModal({
  token,
  onClose,
  onConfirm,
  loading,
}: {
  token: TokenOut | null;
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
}) {
  return (
    <Modal
      open={!!token}
      onClose={onClose}
      title={<span className="flex items-center gap-2 text-[var(--color-danger)]"><ShieldAlert size={16} />Отзыв токена</span>}
      subtitle={token ? `${token.name} · #${token.id}` : ""}
      size="sm"
      footer={
        <>
          <Btn onClick={onClose}>Отмена</Btn>
          <Btn variant="primary" onClick={onConfirm}>
            {loading ? "Отзыв…" : "Отозвать"}
          </Btn>
        </>
      }
    >
      <p className="text-[13px] text-[var(--color-fg-muted)]">
        Токен будет помечен как отозванный. Все CI-задачи, использующие его, начнут получать 401.
        Действие необратимо.
      </p>
    </Modal>
  );
}

function fmtDate(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}
